// VolumeController.jsx
import React, { useCallback, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { throttle } from 'lodash';

import Seekbar from '../component/Seekbar.jsx';

import { ButtonId } from '../resource/trackingConstants.js';
import { NULL_FUNCTION } from '../resource/defaults.js';

import ResetButtonStyle from '../style/ResetButtonStyle.js';

import AudioOnIconSrc from '../../img/ic-audio-on.svg';
import AudioOffIconSrc from '../../img/ic-audio-off.svg';

import { color } from '../style/variables.js';

const VOLUME_THROTTLE_MSEC = 25;

export const VolumeController = ({
  volume = 1,
  isMuted = true,
  isRenderSlider = false,
  isDragToMute = false,
  volumeBeforeDragToMute = null,
  setPlayerData = NULL_FUNCTION,
}) => {
  // Throttle setPlayerData to reduce Redux dispatch frequency during drag
  const setPlayerDataThrottled = useMemo(
    () =>
      throttle(setPlayerData, VOLUME_THROTTLE_MSEC, {
        leading: true,
        trailing: true,
      }),
    [setPlayerData]
  );

  // Cleanup throttle on unmount
  useEffect(() => {
    return () => setPlayerDataThrottled.cancel();
  }, [setPlayerDataThrottled]);

  const handleMuteOnClick = useCallback(() => {
    if (isMuted) {
      setPlayerData({ isMuted: false, isFirstUnmuted: false });
      if (isDragToMute) {
        setPlayerData({ volume: volumeBeforeDragToMute, isDragToMute: false });
      }
    } else {
      setPlayerData({ isMuted: true, isFirstUnmuted: false });
    }
  }, [isMuted, setPlayerData, isDragToMute, volumeBeforeDragToMute]);

  // record volume before drag to 0 (mute)
  const handleSeekStart = useCallback(() => {
    setPlayerData({ volumeBeforeDragToMute: volume });
  }, [volume, setPlayerData]);

  const handleVolumeChange = useCallback(
    ({ positionPercentage }) => {
      const parsedVolume = parseFloat(positionPercentage);
      if (parsedVolume <= 0) {
        setPlayerData({
          isDragToMute: true,
          isMuted: true,
          isFirstUnmuted: false,
        });
      }
      if (isMuted && parsedVolume > 0) {
        if (isDragToMute) {
          setPlayerData({ isDragToMute: false });
        }
        setPlayerData({ isMuted: false, isFirstUnmuted: false });
      }
      setPlayerDataThrottled({ volume: parsedVolume });
    },
    [setPlayerDataThrottled, isMuted, isDragToMute, setPlayerData]
  );

  return (
    <StyledVolumeController>
      <MuteButton
        data-element_id={
          isMuted ? ButtonId.All.ButtonMuted : ButtonId.All.ButtonVolume
        }
        onClick={handleMuteOnClick}
      >
        <img src={isMuted ? AudioOffIconSrc : AudioOnIconSrc} alt="Mute" />
      </MuteButton>
      {isRenderSlider && (
        <VolumeSliderWrapper>
          <Seekbar
            thumbSize={12}
            seekbarHeight={2}
            value={isMuted ? 0 : volume}
            min={0}
            max={1}
            onChange={handleVolumeChange}
            onSeekStart={handleSeekStart}
            currentValueColor={color.white}
            isShowHoverFeedback
          />
        </VolumeSliderWrapper>
      )}
    </StyledVolumeController>
  );
};

VolumeController.propTypes = {
  volume: PropTypes.number,
  isMuted: PropTypes.bool,
  isRenderSlider: PropTypes.bool,
  isDragToMute: PropTypes.bool,
  volumeBeforeDragToMute: PropTypes.number,
  setPlayerData: PropTypes.func,
};

const StyledVolumeController = styled.div`
  min-height: 36px;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  cursor: pointer;
  gap: 10.5px;
`;

const ControlButton = styled.button`
  ${ResetButtonStyle};
  min-width: 36px;
  min-height: 36px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  > img {
    object-fit: contain;
  }
`;

const MuteButton = styled(ControlButton)`
  :hover {
    background-color: ${color.neutral['100_alpha_20']};
  }
  transition: background-color 0.1s ease-in-out;
  > img {
    width: 24px;
    height: 24px;
  }
`;

const VolumeSliderWrapper = styled.div`
  position: relative;
  width: 43px;
  height: 32px;
  display: flex;
  align-items: center;
`;

export default VolumeController;
